import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Habit, HabitCompletion, HabitScore } from '../../services/habitService';
import AddHabitModal from './AddHabitModal';
import './HabitSection.css';

interface HabitSectionProps {
  showSection: boolean;
  onToggleSection: () => void;
  habits: Habit[];
  habitCompletions: { [habitId: number]: HabitCompletion | boolean };
  habitScores?: { [habitId: number]: HabitScore };
  loading: boolean;
  onToggleCompletion: (habitId: number) => void;
  onHabitsReload: () => void;
  onError: (error: string) => void;
  userId: number;
  showOnlyIncomplete?: boolean;
  onToggleShowOnlyIncomplete?: () => void;
}

const HabitSection: React.FC<HabitSectionProps> = ({
  showSection,
  onToggleSection,
  habits,
  habitCompletions,
  habitScores = {},
  loading,
  onToggleCompletion,
  onHabitsReload,
  onError,
  userId,
  showOnlyIncomplete = false,
  onToggleShowOnlyIncomplete
}) => {
  const navigate = useNavigate();
  const [showAddHabitModal, setShowAddHabitModal] = useState(false);

  const handleAddHabit = () => {
    setShowAddHabitModal(true);
  };

  const handleHabitAdded = () => {
    onHabitsReload();
  };

  const handleHabitClick = (habitId: number) => {
    navigate(`/habits/${habitId}`);
  };

  const handleCheckboxClick = (e: React.MouseEvent, habitId: number) => {
    e.stopPropagation();
    onToggleCompletion(habitId);
  };

  // Progress number component
  const ProgressNumber: React.FC<{ score?: HabitScore }> = ({ score }) => {
    if (!score) {
      return <span className="habit-progress-number-days" style={{ color: '#999999' }}>0%</span>;
    }

    // Convert 0-1 score to percentage (0-100)
    const percentage = Math.round(score.score * 100);
    const getScoreColor = (scoreValue: number) => {
      // scoreValue is now between 0-1, so we need to convert to 0-100 for comparison
      const percentageValue = scoreValue * 100;
      if (percentageValue >= 80) return '#4CAF50'; // Green
      if (percentageValue >= 60) return '#FF9800'; // Orange
      if (percentageValue >= 40) return '#FFC107'; // Yellow
      return '#F44336'; // Red
    };

    return (
      <span 
        className="habit-progress-number-days" 
        style={{ color: getScoreColor(score.score) }}
      >
        {percentage}%
      </span>
    );
  };

  // Split habits into health and other categories
  const healthHabits = habits.filter(h => h.category === 'health');
  const otherHabits = habits.filter(h => h.category !== 'health');

  // Render a subsection of habits
  const renderHabitSubsection = (subsectionHabits: Habit[], title: string) => {
    if (subsectionHabits.length === 0) return null;

    const completedCount = subsectionHabits.filter(habit => {
      const completion = habitCompletions[habit.id];
      return typeof completion === 'object' 
        ? Boolean(completion.is_completed)
        : Boolean(completion);
    }).length;

    return (
      <div className="habit-subsection-days">
        <div className="habit-subsection-header-days">
          {title}
          <span className="habit-subsection-count-days">
            {completedCount} / {subsectionHabits.length}
          </span>
        </div>
        <div className="habit-list-days">
          {subsectionHabits.map((habit) => {
            const completion = habitCompletions[habit.id];
            const isCompleted = typeof completion === 'object' 
              ? Boolean(completion.is_completed)
              : Boolean(completion);
            const score = habitScores[habit.id];
            
            return (
              <div 
                key={habit.id} 
                className={`habit-item-days ${isCompleted ? 'completed' : ''}`}
                onClick={() => handleHabitClick(habit.id)}
              >
                <div className="habit-item-content-days">
                  <button
                    className={`habit-checkbox-days ${isCompleted ? 'completed' : ''}`}
                    onClick={(e) => handleCheckboxClick(e, habit.id)}
                  >
                    <span className="habit-checkbox-mark-days">{isCompleted ? '✓' : ''}</span>
                  </button>
                  <span className="habit-icon-days">{habit.icon}</span>
                  <span className="habit-name-days">{habit.name}</span>
                  <ProgressNumber score={score} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
    <div className="habit-section-days">
      <button 
        className="habit-section-toggle-days"
        onClick={onToggleSection}
      >
        <span className="habit-toggle-text-days">
          /habit
          {habits.length > 0 && (
            <span className="habit-count-days">
              {' - '}
              {Object.values(habitCompletions).filter(c => 
                typeof c === 'object' ? Boolean(c.is_completed) : Boolean(c)
              ).length}
              {' / '}
              {habits.length}
            </span>
          )}
        </span>
        <span className="habit-toggle-arrow-days">{showSection ? '▼' : '▶'}</span>
      </button>
      
      <div 
        className={`habit-section-content-days ${showSection ? 'expanded' : 'collapsed'}`}
        style={{
          maxHeight: showSection ? '2000px' : '0px',
          paddingTop: showSection ? '1rem' : '0px',
          paddingBottom: showSection ? '1rem' : '0px',
        }}
      >
        {/* Habit Actions */}
        <div className="habit-actions-days">
          <button 
            className="habit-add-action-btn-days"
            onClick={handleAddHabit}
            title="Add new habit"
          >
            + Add Habit
          </button>
          {onToggleShowOnlyIncomplete && (
            <button 
              className="habit-filter-toggle-btn-days"
              onClick={onToggleShowOnlyIncomplete}
              title={showOnlyIncomplete ? "Show all habits" : "Show only incomplete habits"}
              style={{
                backgroundColor: showOnlyIncomplete ? '#4CAF50' : '#f0f0f0',
                color: showOnlyIncomplete ? '#ffffff' : '#333333',
              }}
            >
              {showOnlyIncomplete ? '✓ Incomplete Only' : 'Show All'}
            </button>
          )}
          <button 
            className="habit-view-all-btn-days"
            onClick={() => navigate('/habits')}
            title="View all habits"
          >
            View All Habits
          </button>
        </div>

        {loading ? (
          <div className="habit-loading-days">Loading habits...</div>
        ) : habits.length > 0 ? (
          <>
            {renderHabitSubsection(healthHabits, 'Health')}
            {renderHabitSubsection(otherHabits, 'Other Habits')}
          </>
        ) : (
          <div className="habit-empty-days">No habits found for this date</div>
        )}
      </div>
    </div>

    {showAddHabitModal && (
      <AddHabitModal
        userId={userId}
        onClose={() => setShowAddHabitModal(false)}
        onHabitAdded={handleHabitAdded}
        onError={onError}
      />
    )}
    </>
  );
};

export default HabitSection;

